/* eslint-disable react-refresh/only-export-components */
import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/shared/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-[7px] text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-ring)] focus-visible:ring-offset-0 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-[13px] [&_svg]:shrink-0 aria-invalid:ring-2 aria-invalid:ring-[var(--bad)]/30 aria-invalid:border-[var(--bad)] shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-brand-accent text-white border border-brand-accent shadow-[inset_0_1px_0_rgba(255,255,255,0.15),0_1px_2px_var(--brand-ring)] hover:bg-brand-accent-hi hover:border-brand-accent-hi",
        destructive:
          "bg-bad text-white border border-bad shadow-[var(--shadow-sm)] hover:opacity-90",
        outline:
          "bg-surface text-ink-2 border border-line-strong hover:bg-hover",
        secondary:
          "bg-surface text-ink-2 border border-line-strong shadow-[var(--shadow-sm)] hover:bg-hover",
        ghost:
          "bg-transparent text-ink-2 hover:bg-hover",
        link:
          "bg-transparent text-brand-accent-ink underline-offset-4 hover:underline",
      },
      size: {
        default: "h-auto px-3 py-[7px]",
        xs: "h-auto gap-1 px-2 py-[3px] text-[11px] [&_svg:not([class*='size-'])]:size-3",
        sm: "h-auto px-2.5 py-[5px] text-[12px]",
        lg: "h-auto px-4 py-2 text-[13px]",
        icon: "h-9 w-9 p-0",
        "icon-xs": "h-6 w-6 p-0 [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "h-8 w-8 p-0",
        "icon-lg": "h-10 w-10 p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
